import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CampaignInfo from '../FlowchartView/components/FlowchartHUD/CampaignInfo';
import { computeMissionHudLists } from '../FlowchartView/components/FlowchartHUD/utils/missionHudLists';
import { CATEGORY_COLORS } from '../FlowchartView/TaskNode/constants';
import { getPrimaryTag } from '../hooks/useTagActions';
import {
  getStoredAvailableCollapsed,
  saveAvailableCollapsed,
  getStoredActiveCollapsed,
  saveActiveCollapsed,
} from '../FlowchartView/components/FlowchartHUD/utils/missionsStorage';
import { useCampaignProgressStats } from '../shared/CampaignHeader/hooks/useCampaignProgressStats';
import FokusOverlay from '../shared/FokusOverlay';
import { buildGraph } from './buildGraph';
import { quantizeLayoutAspect } from './neuralLayout';
import { NeuralFloatingCard } from './components/NeuralFloatingCard';
import { NeuralMissionHud } from './components/NeuralMissionHud';
import { useNeuralScene } from './hooks/useNeuralScene';
import { IGNITION_DURATION_MS } from './setupNeuralScene/createIgnitionEffects';
import { createIgnitionSnapshot, detectUnlockIgnition } from './utils/unlockIgnition';
import { isStatusBlocked, isStatusCompleted } from '../FlowchartView/utils/statusUtils';
import './NeuralView.css';

const NeuralView = ({
  tasks = [],
  statuses = [],
  sprintName = '',
  sprintIcon,
  sprintProgress = 0,
  breadcrumbPath = [],
  onSelectSprint,
  onRename,
  onIconChange,
  lockStates = {},
  nestedSprints = [],
  allSprints = [],
  campaignId = '',
  kanbanProps = null,
  cardDisplaySettings,
  viewApiRef,
  /** Lifted selection (SprintDetail state) — drives the toolbar's Fokus button. */
  selectedTaskIds = null,
  onSelectionChange = null,
  isFokusMode = false,
}) => {
  const hostRef = useRef(null);
  const [layoutAspect, setLayoutAspect] = useState(1.6);
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const apply = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w < 8 || h < 8) return false;
      const next = quantizeLayoutAspect(w / h);
      setLayoutAspect((prev) => (prev === next ? prev : next));
      return true;
    };
    if (apply()) return undefined;
    const ro = new ResizeObserver(() => {
      if (apply()) ro.disconnect();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const floatingCardWrapRef = useRef(null);
  const focusMissionRef = useRef(null);
  const neuralSceneApiRef = useRef(null);
  const setSelectedMissionIdRef = useRef(() => {});
  const renameTagRef = useRef(null);
  renameTagRef.current = kanbanProps?.onRenameTag || kanbanProps?.onRenameCategory || null;
  const previousSnapshotRef = useRef(null);
  const [ignitionEvent, setIgnitionEvent] = useState(null);
  const ignitionTimersRef = useRef([]);

  useEffect(() => () => {
    ignitionTimersRef.current.forEach(clearTimeout);
  }, []);

  const [selectedMissionId, setSelectedMissionId] = useState(null);
  const selectedMissionIdRef = useRef(null);
  selectedMissionIdRef.current = selectedMissionId;
  const [localAvailableCollapsed, setLocalAvailableCollapsed] = useState(() =>
    getStoredAvailableCollapsed(campaignId),
  );
  const [localActiveCollapsed, setLocalActiveCollapsed] = useState(() =>
    getStoredActiveCollapsed(campaignId),
  );

  useEffect(() => {
    setLocalAvailableCollapsed(getStoredAvailableCollapsed(campaignId));
    setLocalActiveCollapsed(getStoredActiveCollapsed(campaignId));
  }, [campaignId]);

  useEffect(() => {
    if (!selectedMissionId) return;
    const visible = (tasks ?? []).some((t) => t.id === selectedMissionId);
    if (!visible) setSelectedMissionId(null);
  }, [tasks, selectedMissionId]);

  // Mirror the single selected mission into SprintDetail's selection set so the
  // toolbar's Fokus button knows there is something to focus on.
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  useEffect(() => {
    const report = onSelectionChangeRef.current;
    if (!report) return;
    const next = selectedMissionId ? new Set([selectedMissionId]) : new Set();
    const prev = selectedTaskIds;
    const same = prev instanceof Set && prev.size === next.size && [...next].every((id) => prev.has(id));
    if (!same) report(next);
  }, [selectedMissionId, selectedTaskIds]);
  useEffect(() => () => { onSelectionChangeRef.current?.(new Set()); }, []);

  const handleToggleAvailable = useCallback(() => {
    const next = !localAvailableCollapsed;
    setLocalAvailableCollapsed(next);
    saveAvailableCollapsed(campaignId, next);
  }, [campaignId, localAvailableCollapsed]);

  const handleToggleActive = useCallback(() => {
    const next = !localActiveCollapsed;
    setLocalActiveCollapsed(next);
    saveActiveCollapsed(campaignId, next);
  }, [campaignId, localActiveCollapsed]);

  // Island clouds take the tag's own colour; categories are the legacy fallback.
  const tagColorByName = useMemo(() => {
    const source = (kanbanProps?.allTags?.length ? kanbanProps.allTags : kanbanProps?.categories) || [];
    const map = {};
    source.forEach((t) => { if (t?.name && t?.color) map[t.name] = t.color; });
    return map;
  }, [kanbanProps?.allTags, kanbanProps?.categories]);
  const baseGraph = useMemo(
    () => buildGraph(tasks ?? [], statuses ?? [], { nestedSprints, allSprints, tagColorByName, aspect: layoutAspect }),
    [tasks, statuses, nestedSprints, allSprints, tagColorByName, layoutAspect],
  );
  const { activeTasks, blockedTasks, availableTasks, waitingTasks } = useMemo(
    () => computeMissionHudLists(tasks ?? [], statuses ?? [], lockStates, nestedSprints, allSprints),
    [tasks, statuses, lockStates, nestedSprints, allSprints],
  );
  const graph = useMemo(() => {
    const availableIds = new Set(availableTasks.map((t) => t.id));
    return {
      ...baseGraph,
      nodes: baseGraph.nodes.map((node) => ({
        ...node,
        isLocked: !!lockStates[node.id]?.isLocked,
        // Satellites (nested campaign tasks) dim with their hub when it's excluded.
        isExcluded: !!lockStates[node.id]?.isExcluded || (!!node.parentTaskId && !!lockStates[node.rootTaskId]?.isExcluded),
        isAvailable: availableIds.has(node.id) && !lockStates[node.id]?.isExcluded,
      })),
    };
  }, [baseGraph, lockStates, availableTasks]);
  const graphRef = useRef(graph);
  graphRef.current = graph;

  const handleHudGoToMission = useCallback((taskId) => {
    setSelectedMissionId(taskId ?? null);
    if (taskId) focusMissionRef.current?.(taskId);
  }, []);

  // Toolbar Search dispatches `focusMission` (same event Flow/Kanban use).
  // Available / Active lists call this directly; Search never reached the camera.
  useEffect(() => {
    const onFocusMission = (event) => {
      const { missionId, view } = event.detail || {};
      if (view !== 'neural' || !missionId) return;
      handleHudGoToMission(missionId);
    };
    window.addEventListener('focusMission', onFocusMission);
    return () => window.removeEventListener('focusMission', onFocusMission);
  }, [handleHudGoToMission]);

  setSelectedMissionIdRef.current = setSelectedMissionId;
  const {
    completedCount,
    totalCount,
    remainingMinutes,
    remainingMinMinutes,
    missingEstCount,
    remainingBreakdown,
  } = useCampaignProgressStats(tasks, statuses, nestedSprints, allSprints);

  const selectedTask = useMemo(
    () => (selectedMissionId ? tasks?.find((t) => t.id === selectedMissionId) ?? null : null),
    [tasks, selectedMissionId],
  );

  const floatingCardModel = useMemo(() => {
    if (!selectedTask || !kanbanProps) return null;
    const kp = kanbanProps;
    const availableTags = (kp.allTags?.length ? kp.allTags : kp.categories) || [];
    const primaryTagName = getPrimaryTag(selectedTask);
    const currentCategory = primaryTagName
      ? availableTags.find((t) => t.name === primaryTagName) || { name: primaryTagName, color: CATEGORY_COLORS[0] }
      : null;
    const lockState = lockStates[selectedTask.id];
    const isBlocked = isStatusBlocked(selectedTask.status, statuses);
    const isLocked = lockState?.isLocked && !isStatusCompleted(selectedTask.status, statuses);
    const isExcluded = lockState?.isExcluded;
    const nestedSprint = nestedSprints.find((ns) => ns.id === selectedTask.nestedSprintId);
    return {
      kp,
      currentCategory,
      lockState,
      isBlocked,
      isLocked,
      isExcluded,
      nestedSprint,
      task: selectedTask,
    };
  }, [selectedTask, kanbanProps, lockStates, statuses, nestedSprints]);

  const handleGoToDependencyInGraph = useCallback((depId) => {
    if (!depId) return;
    setSelectedMissionId(depId);
    focusMissionRef.current?.(depId);
  }, []);

  useNeuralScene({
    graphRef,
    hostRef,
    floatingCardWrapRef,
    focusMissionRef,
    neuralSceneApiRef,
    setSelectedMissionIdRef,
    selectedMissionIdRef,
    renameTagRef,
  });

  useEffect(() => {
    neuralSceneApiRef.current?.refreshPresentation?.();
  }, [graph]);

  useEffect(() => {
    setSelectedMissionId(null);
  }, [campaignId]);

  useLayoutEffect(() => {
    let raf = 0;
    let cancelled = false;
    let tries = 0;
    const go = () => {
      if (cancelled) return;
      const host = hostRef.current;
      const api = neuralSceneApiRef.current;
      const ready = host && host.clientWidth >= 32 && host.clientHeight >= 32 && api;
      if (!ready && tries < 60) {
        tries += 1;
        raf = requestAnimationFrame(go);
        return;
      }
      if (!api) return;
      if (api.requestOverview) api.requestOverview();
      else api.frameOverview?.();
    };
    raf = requestAnimationFrame(go);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [campaignId, layoutAspect, graph.layoutKey, graph.nodes.length]);

  useEffect(() => {
    neuralSceneApiRef.current?.syncSelection(selectedMissionId);
  }, [selectedMissionId]);

  useEffect(() => {
    if (!viewApiRef) return undefined;
    viewApiRef.current = {
      recentre() {
        setSelectedMissionId(null);
        neuralSceneApiRef.current?.frameOverview();
      },
    };
    return () => {
      viewApiRef.current = null;
    };
  }, [viewApiRef]);

  useEffect(() => {
    const next = createIgnitionSnapshot({ campaignId, tasks, statuses, lockStates, availableTasks, nestedSprints, graph });
    const previous = previousSnapshotRef.current;
    const event = detectUnlockIgnition(previous, next);
    previousSnapshotRef.current = next;
    const invalidated = previous && (previous.campaignId !== next.campaignId || previous.context !== next.context
      || [...previous.completed].some((id) => !next.completed.has(id)));
    if (invalidated || event) {
      ignitionTimersRef.current.forEach(clearTimeout);
      ignitionTimersRef.current = [];
      neuralSceneApiRef.current?.clearIgnition();
      setIgnitionEvent(null);
    }
    if (!event) return;
    neuralSceneApiRef.current?.ignite(event);
    setIgnitionEvent(event);
    ignitionTimersRef.current.push(setTimeout(() => setIgnitionEvent(null), IGNITION_DURATION_MS));
  }, [campaignId, tasks, statuses, lockStates, availableTasks, nestedSprints, graph]);

  return (
    <div className={`neural-view${ignitionEvent ? ' neural-view--igniting' : ''}${isFokusMode ? ' neural-view--fokus-mode' : ''}`}>
      <div className="neural-view__main">
        {isFokusMode && <FokusOverlay skipEnterAnimation />}
        <div className="neural-view__hud">
          <CampaignInfo
            sprintName={sprintName}
            sprintIcon={sprintIcon}
            progress={sprintProgress}
            completedCount={completedCount}
            totalCount={totalCount}
            remainingMinutes={remainingMinutes}
            remainingMinMinutes={remainingMinMinutes}
            missingEstCount={missingEstCount}
            remainingBreakdown={remainingBreakdown}
            breadcrumbPath={breadcrumbPath}
            onSelectSprint={onSelectSprint}
            onRename={onRename}
            onIconChange={onIconChange}
          />
        </div>
        <NeuralMissionHud
          activeTasks={activeTasks}
          blockedTasks={blockedTasks}
          waitingTasks={waitingTasks}
          availableTasks={availableTasks}
          localActiveCollapsed={localActiveCollapsed}
          localAvailableCollapsed={localAvailableCollapsed}
          onToggleActive={handleToggleActive}
          onToggleAvailable={handleToggleAvailable}
          onGoToTask={handleHudGoToMission}
          selectedMissionId={selectedMissionId}
        />
        <div
          className="neural-view__canvas-host"
          ref={hostRef}
          role="presentation"
          aria-label="Campaign dependency graph (neural view)"
        />
        <NeuralFloatingCard
          model={floatingCardModel}
          wrapRef={floatingCardWrapRef}
          nestedSprints={nestedSprints}
          onGoToDependencyInGraph={handleGoToDependencyInGraph}
          cardDisplaySettings={cardDisplaySettings}
        />
        {ignitionEvent && (
          <div className="neural-view__ignition-caption" role="status" aria-live="polite">
            <span className="neural-view__ignition-count">{ignitionEvent.targetIds.length}</span>
            <span>{ignitionEvent.targetIds.length === 1 ? 'task unlocked' : 'tasks unlocked'}</span>
          </div>
        )}
        <div className="neural-view__top-gradient" aria-hidden="true" />
      </div>
    </div>
  );
};

export default NeuralView;
